import React, { createContext, useContext, useState } from "react";
import { completeCollection, SessionStats } from "../api";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Interface defining the completion context functionality
 */
interface CompletionContextType {
  /** Record of completion counts by collection ID */
  completionCounts: { [key: number]: number };
  /** Function to increment completion count for a collection */
  updateCompletionCount: (collectionId: number, stats?: SessionStats) => void;
}

const CompletionContext = createContext<CompletionContextType | undefined>(
  undefined,
);

/**
 * Provider component that manages collection completion state.
 * Tracks completion counts and integrates with Auth0 for authentication.
 * Handles API calls to record collection completions.
 *
 * @param props - The provider props
 * @returns A context provider for completion tracking
 */
export const CompletionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [completionCounts, setCompletionCounts] = useState<{
    [key: number]: number;
  }>({});

  const updateCompletionCount = async (
    collectionId: number,
    stats?: SessionStats,
  ) => {
    try {
      await completeCollection(collectionId, getAccessTokenSilently, stats);
      setCompletionCounts((prevCounts) => ({
        ...prevCounts,
        [collectionId]: (prevCounts[collectionId] || 0) + 1,
      }));
    } catch (error) {
      console.error("Failed to record completion:", error);
    }
  };

  return (
    <CompletionContext.Provider
      value={{ completionCounts, updateCompletionCount }}
    >
      {children}
    </CompletionContext.Provider>
  );
};

export const useCompletion = () => {
  const context = useContext(CompletionContext);
  if (!context) {
    throw new Error("useCompletion must be used within a CompletionProvider");
  }
  return context;
};
